import React from 'react';
import { Divider, Grid } from '@material-ui/core';
import type { Theme } from '@material-ui/core';
import { filesize } from 'filesize';

import { useCurrentUser } from '@klp/auth';
import { Block, Typography } from '@klp/design';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { KlpPalette } from '@klp/design/Theme/KlpPalette';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { FileDownload } from '@klp/filedownload';
import { fromUTC, formatDateTime } from '@klp/helpers';
import { IFrame } from '@klp/iframe';

import { sakApiRoutes } from '../api';
import type { Saksmelding } from '../classes';

const styles = (theme: Theme) => ({
    entryContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },

    authorEntryContainer: {
        alignItems: 'flex-end',
        '& > div': {
            backgroundColor: KlpPalette.tertiary.veryLight,
        },
    },

    entry: {
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: theme.palette.primary.medium,
        borderRadius: '5px',
        backgroundColor: KlpPalette.primary.veryLight,
        width: '100%',
    },

    previewDownload: {
        '&:hover, &:active': {
            textDecoration: 'underline',
            cursor: 'pointer',
        },
    },
    download: {
        textAlign: 'center',
        flexDirection: 'row',
        paddingLeft: theme.spacing(1),
        '& > *': {
            display: 'inline-block',
            '& > *': {
                margin: theme.spacing(1),
            },
        },
    },
});

interface IProps {
    saksmelding: Saksmelding;
}

const isAllowedFileType = (fileName: string | undefined) => {
    if (
        fileName?.endsWith('.png') ||
        fileName?.endsWith('.jpg') ||
        fileName?.endsWith('.jpeg') ||
        fileName?.endsWith('.gif') ||
        fileName?.endsWith('.jfif') ||
        fileName?.endsWith('.pdf')
    ) {
        return true;
    } else {
        return false;
    }
};

const renderVedleggList = (
    saksmelding: Saksmelding,
    classes: ReturnType<typeof useKlpStyles>,
) => {
    return saksmelding.vedleggSakList.map((vedlegg) => (
        <Grid
            item={true}
            xs={12}
            key={`${vedlegg.saksmeldingId}-${vedlegg.id}-list-item`}
        >
            <Grid
                container={true}
                alignItems="center"
                justifyContent="flex-end"
            >
                <Grid item={true}>
                    <Block klpTypographyVariant="small">
                        <span>{vedlegg.fileName}</span>(
                        <span>{filesize(vedlegg.fileSize)}</span>)
                    </Block>
                </Grid>
                <Grid item={true} className={classes.download}>
                    {isAllowedFileType(vedlegg.fileName) && (
                        <FileDownload
                            path={sakApiRoutes.sakMeldingAttachment(
                                saksmelding.id,
                                vedlegg.id,
                            )}
                            name={vedlegg.fileName}
                            preview={true}
                        >
                            <Illustration
                                className={classes.previewDownload}
                                icon={IconName.SEARCH}
                                klpPalette="primary"
                                klpColor="main"
                            />
                        </FileDownload>
                    )}
                    <FileDownload
                        path={sakApiRoutes.sakMeldingAttachment(
                            saksmelding.id,
                            vedlegg.id,
                        )}
                        name={vedlegg.fileName}
                    >
                        <Illustration
                            className={classes.previewDownload}
                            icon={IconName.DOWNLOAD}
                            klpPalette="primary"
                            klpColor="main"
                        />
                    </FileDownload>
                </Grid>
            </Grid>
        </Grid>
    ));
};

export const SaksmeldingComponent: React.FC<IProps> = ({ saksmelding }) => {
    const classes = useKlpStyles({}, styles);
    const { currentUser: user } = useCurrentUser();
    const iframeHeight = 450;

    const additionalClasses =
        user && user.email === saksmelding.opprettetAv
            ? classes.authorEntryContainer
            : '';

    return (
        <Block
            klpTypographyVariant="small"
            marginBottom="bulky"
            className={`${classes.entryContainer} ${additionalClasses}`}
        >
            <Typography klpTypographyVariant="xsmall">
                {saksmelding.opprettetAv}{' '}
                {saksmelding.created && (
                    <span>{formatDateTime(fromUTC(saksmelding?.created))}</span>
                )}
            </Typography>
            <Block padding="tiny" className={classes.entry}>
                <IFrame
                    content={saksmelding.melding}
                    minHeight={iframeHeight}
                    extraMargin={26}
                    autoHeight={true}
                />
                {saksmelding.vedleggSakList.length > 0 && (
                    <Grid container={true}>
                        <Grid item={true} xs={12}>
                            <Block marginTop="compact">
                                <Divider />
                            </Block>
                            {renderVedleggList(saksmelding, classes)}
                        </Grid>
                    </Grid>
                )}
            </Block>
        </Block>
    );
};
